import React, { useEffect, useMemo } from 'react';
import { Box, Container, Grid, Typography, Divider, Paper, Stack, Chip, Card, Button, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import ChatIcon from '@mui/icons-material/Chat';
import MessageIcon from '@mui/icons-material/Message';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FunctionsIcon from '@mui/icons-material/Functions';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import UpdateIcon from '@mui/icons-material/Update';
import TimerIcon from '@mui/icons-material/Timer';
import SendIcon from '@mui/icons-material/Send';
import ReplyIcon from '@mui/icons-material/Reply';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAppDispatch, useAppSelector } from 'src/store';
import {
  fetchConversationAnalytics,
  fetchMessageAnalytics,
  fetchStatusChangeAnalytics,
  updateFilters,
  resetFilters,
  exportConversationsCSV,
  exportMessagesCSV
} from 'src/features/analytics/dataAnalytics.slice';
import MetricCard from 'src/components/Analytics/MetricCard';
import FilterBar from 'src/components/Analytics/FilterBar';
import AnalyticsChart from 'src/components/Analytics/AnalyticsChart';
import ConversationStatusChart from 'src/components/Analytics/ConversationStatusChart';
import StatusChangesSection from 'src/components/Analytics/StatusChangesSection';
import { AnalyticsFilters, ChartData } from 'src/types/analytics';

// Helper function to format seconds into a readable format
const formatTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
};

// Helper function to format labels by replacing underscores with spaces and capitalizing
const formatLabel = (label: string): string => {
  return label
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const AnalyticsDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppSelector(state => state.user);
  const { channels } = useAppSelector(state => state.channels);
  const {
    conversationAnalytics,
    messageAnalytics,
    filters,
    loading,
    error
  } = useAppSelector(state => state.dataAnalytics);

  useEffect(() => {
    // Fetch analytics data when component mounts or filters change
    dispatch(fetchConversationAnalytics(filters));
    dispatch(fetchMessageAnalytics(filters));
    dispatch(fetchStatusChangeAnalytics(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (error.conversationsCsvExport) {
      enqueueSnackbar(`Error exporting conversations: ${error.conversationsCsvExport}`, {
        variant: 'error',
        autoHideDuration: 5000
      });
    }
  }, [error.conversationsCsvExport, enqueueSnackbar]);

  useEffect(() => {
    if (error.messagesCsvExport) {
      enqueueSnackbar(`Error exporting messages: ${error.messagesCsvExport}`, {
        variant: 'error',
        autoHideDuration: 5000
      });
    }
  }, [error.messagesCsvExport, enqueueSnackbar]);

  const handleFiltersChange = (newFilters: Partial<AnalyticsFilters>) => {
    dispatch(updateFilters(newFilters));
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleExportConversations = () => {
    dispatch(exportConversationsCSV(filters))
      .unwrap()
      .then((result) => {
        if (result && result.success) {
          enqueueSnackbar(`Successfully exported to ${result.filename}`, {
            variant: 'success',
            autoHideDuration: 3000
          });
        }
      })
      .catch(() => {/* Error handled in useEffect */ });
  };

  const handleExportMessages = () => {
    dispatch(exportMessagesCSV(filters))
      .unwrap()
      .then((result) => {
        if (result && result.success) {
          enqueueSnackbar(`Successfully exported to ${result.filename}`, {
            variant: 'success',
            autoHideDuration: 3000
          });
        }
      })
      .catch(() => {/* Error handled in useEffect */ });
  };

  // Available channels for filtering
  const availableChannels = useMemo(() => {
    return channels.map(channel => ({
      id: channel.id,
      name: channel.chatbot_name || channel.title || `Channel ${channel.id}`
    }));
  }, [channels]);

  // Available statuses for filtering
  const availableStatuses = useMemo(() => {
    const statusesSet = new Set<string>();

    // Extract all statuses from conversation analytics
    if (conversationAnalytics?.statusDistribution) {
      Object.keys(conversationAnalytics.statusDistribution).forEach(status => {
        statusesSet.add(status);
      });
    }

    // Extract all statuses from message analytics
    if (messageAnalytics?.statusDistribution) {
      Object.keys(messageAnalytics.statusDistribution).forEach(status => {
        statusesSet.add(status);
      });
    }

    return Array.from(statusesSet).map(status => ({
      id: status,
      name: status.charAt(0).toUpperCase() + status.slice(1)
    }));
  }, [conversationAnalytics, messageAnalytics]);

  // Format data for time-based charts
  const conversationsTimeChartData: ChartData = useMemo(() => {
    // We need to check if we have the timeBasedData array from our transformed data
    if (!conversationAnalytics?.timeBasedData || conversationAnalytics.timeBasedData.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Sort the data by period for chronological order
    const sortedData = [...conversationAnalytics.timeBasedData].sort((a, b) =>
      new Date(a.period).getTime() - new Date(b.period).getTime()
    );

    return {
      labels: sortedData.map(item => item.period),
      datasets: [
        {
          label: 'Conversations',
          data: sortedData.map(item => item.count),
          borderColor: '#3f51b5',
          backgroundColor: 'rgba(63, 81, 181, 0.2)',
          fill: true
        }
      ]
    };
  }, [conversationAnalytics]);

  // Format data for message time-based chart
  const messagesTimeChartData: ChartData = useMemo(() => {
    if (!messageAnalytics?.timeBasedData || messageAnalytics.timeBasedData.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Sort the data by period for chronological order
    const sortedData = [...messageAnalytics.timeBasedData].sort((a, b) =>
      new Date(a.period).getTime() - new Date(b.period).getTime()
    );

    return {
      labels: sortedData.map(item => item.period),
      datasets: [
        {
          label: 'Messages',
          data: sortedData.map(item => item.count),
          borderColor: '#f50057',
          backgroundColor: 'rgba(245, 0, 87, 0.2)',
          fill: true
        }
      ]
    };
  }, [messageAnalytics]);

  // Format data for conversation status distribution chart
  const conversationStatusChartData: ChartData = useMemo(() => {
    if (!conversationAnalytics?.statusDistribution) {
      return { labels: [], datasets: [] };
    }

    const statuses = Object.keys(conversationAnalytics.statusDistribution);
    const counts = statuses.map(status => conversationAnalytics.statusDistribution[status]);

    // Short labels for the chart - properly formatted
    const statusLabels = statuses.map(status =>
      formatLabel(status)
    );

    // Detailed labels for the legend
    const legendLabels = statuses.map(status => {
      const formattedStatus = formatLabel(status);
      // You can customize these to be more descriptive if needed
      return `${formattedStatus} Conversations`;
    });

    return {
      labels: statusLabels,
      legendLabels,
      datasets: [
        {
          label: 'Status Distribution',
          data: counts,
          backgroundColor: [
            '#00bcd4',  // Pending/Replied - aqua
            '#f50057',  // Closed - pink
            '#3f51b5',  // Active - blue
            '#ff9800',  // Other statuses - orange
            '#4caf50',  // Other statuses - green
            '#9c27b0'   // Other statuses - purple
          ]
        }
      ]
    };
  }, [conversationAnalytics]);

  // Format data for message role distribution chart
  const messageRoleChartData: ChartData = useMemo(() => {
    if (!messageAnalytics?.roleDistribution) {
      return { labels: [], datasets: [] };
    }

    const roles = Object.keys(messageAnalytics.roleDistribution);
    const counts = roles.map(role => messageAnalytics.roleDistribution[role]);

    // Short labels for the chart
    const roleLabels = roles.map(role => {
      const formattedRole = formatLabel(role);
      return formattedRole === "Assistant" ? "Oppy" : formattedRole;
    });

    // Detailed labels for the legend
    const legendLabels = roles.map(role => {
      const formattedRole = formatLabel(role);
      return formattedRole === 'User' ? 'User Messages' :
        formattedRole === 'Assistant' ? 'Oppy Responses' :
          formattedRole === 'Function' ? 'Function Calls' : `${formattedRole} Messages`;
    });

    return {
      labels: roleLabels,
      legendLabels, // Add legendLabels for the AnalyticsChart component to use
      datasets: [
        {
          label: 'Role Distribution',
          data: counts,
          backgroundColor: [
            '#4caf50',  // User - green
            '#2196f3',  // Assistant/Oppy - blue
            '#ff9800'   // Function - orange
          ]
        }
      ]
    };
  }, [messageAnalytics]);

  // Format data for feedback stats
  const feedbackChartData: ChartData = useMemo(() => {
    if (!conversationAnalytics?.feedbackStats) {
      return { labels: [], datasets: [] };
    }

    const { positive_feedback_count, negative_feedback_count } = conversationAnalytics.feedbackStats;

    // Short labels for the chart
    const feedbackLabels = ['Positive', 'Negative'];

    // Detailed labels for the legend
    const legendLabels = ['Positive Feedback', 'Negative Feedback'];

    return {
      labels: feedbackLabels,
      legendLabels,
      datasets: [
        {
          label: 'Feedback',
          data: [positive_feedback_count, negative_feedback_count],
          backgroundColor: [
            '#4caf50',  // Positive - green
            '#f44336'   // Negative - red
          ]
        }
      ]
    };
  }, [conversationAnalytics]);

  return (
    <Container maxWidth={false}>
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" gutterBottom>
          Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Visualize conversation and message analytics
        </Typography>

        <FilterBar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
          availableChannels={availableChannels}
          availableStatuses={availableStatuses}
          loading={loading.conversations || loading.messages}
        />

        <Typography variant="h5" sx={{ mb: 2 }}>
          Overview
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Conversations"
              value={conversationAnalytics?.totalCount || 0}
              icon={<ChatIcon />}
              loading={loading.conversations}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Messages"
              value={messageAnalytics?.totalCount || 0}
              icon={<MessageIcon />}
              loading={loading.messages}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Avg. Messages per Conversation"
              value={(conversationAnalytics?.messageStats?.avg_messages_per_conversation || 0).toFixed(1)}
              icon={<QueryStatsIcon />}
              loading={loading.conversations}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Avg. Response Time"
              value={formatTime(conversationAnalytics?.messageStats?.avg_response_time_user_to_assistant_seconds || 0)}
              icon={<AccessTimeIcon />}
              loading={loading.conversations}
              subtitle="User to Oppy"
              tooltipText="We analyze appropriate message pairs (message and subsequent response) with valid delivery times and that have an interval smaller than a week for better accuracy."
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Conversation Analytics
          </Typography>
          <Tooltip title="Export conversation data as CSV, it might take a while for larger date ranges and amount of conversations">
            <Button
              variant="outlined"
              startIcon={loading.conversationsCsvExport ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              onClick={handleExportConversations}
              disabled={loading.conversationsCsvExport}
              size="small"
            >
              {loading.conversationsCsvExport ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Tooltip>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <AnalyticsChart
              title="Conversation Trend"
              data={conversationsTimeChartData}
              type="line"
              loading={loading.conversations}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ConversationStatusChart
              title="Conversation Status"
              data={conversationStatusChartData}
              loading={loading.conversations}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          {conversationAnalytics?.messageStats && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Avg. User Messages"
                  value={conversationAnalytics.messageStats.avg_user_messages.toFixed(1)}
                  icon={<PersonIcon />}
                  loading={loading.conversations}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Avg. Oppy Messages"
                  value={conversationAnalytics.messageStats.avg_assistant_messages.toFixed(1)}
                  icon={<SmartToyIcon />}
                  loading={loading.conversations}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Avg. Function Messages"
                  value={conversationAnalytics.messageStats.avg_function_messages.toFixed(1)}
                  icon={<FunctionsIcon />}
                  loading={loading.conversations}
                  tooltipText="Function messages are visible in advanced mode and are responsible for executing the advanced logic behind Oppy's capabilities."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Messages"
                  value={conversationAnalytics.messageStats.total_messages}
                  icon={<MessageIcon />}
                  loading={loading.conversations}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Response Time Analytics
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="User → Oppy"
              value={formatTime(messageAnalytics?.timingStats?.avg_response_time_user_to_assistant_seconds || 0)}
              icon={<SendIcon />}
              tooltipText="We analyze appropriate message pairs (message and subsequent response) with valid delivery times and that have an interval smaller than a week for better accuracy."
              loading={loading.messages}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Oppy → User"
              value={formatTime(messageAnalytics?.timingStats?.avg_response_time_assistant_to_user_seconds || 0)}
              icon={<ReplyIcon />}
              tooltipText="We analyze appropriate message pairs (message and subsequent response) with valid delivery times and that have an interval smaller than a week for better accuracy."
              loading={loading.messages}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="User Response Rate"
              value={`${(messageAnalytics?.timingStats?.response_rate_user_to_assistant_percentage || 0).toFixed(1)}%`}
              icon={<ReplyIcon />}
              tooltipText="Percentage of Oppy messages that received a response from users"
              loading={loading.messages}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Oppy Response Rate"
              value={`${(messageAnalytics?.timingStats?.response_rate_assistant_to_user_percentage || 0).toFixed(1)}%`}
              icon={<SendIcon />}
              tooltipText="Percentage of user messages that received a response from Oppy"
              loading={loading.messages}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Message Analytics
          </Typography>
          <Tooltip title="Export message data as CSV, it might take a while for larger date ranges and amount of messages">
            <Button
              variant="outlined"
              startIcon={loading.messagesCsvExport ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              onClick={handleExportMessages}
              disabled={loading.messagesCsvExport}
              size="small"
            >
              {loading.messagesCsvExport ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Tooltip>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <AnalyticsChart
              title="Message Trend"
              data={messagesTimeChartData}
              type="line"
              loading={loading.messages}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AnalyticsChart
              title="Message Role Distribution"
              data={messageRoleChartData}
              type="pie"
              loading={loading.messages}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Message Analytics
          </Typography>
          <Tooltip title="Export message data as CSV, it might take a while for larger date ranges and amount of messages">
            <Button
              variant="outlined"
              startIcon={loading.messagesCsvExport ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              onClick={handleExportMessages}
              disabled={loading.messagesCsvExport}
              size="small"
            >
              {loading.messagesCsvExport ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Tooltip>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <AnalyticsChart
              title="Message Trend"
              data={messagesTimeChartData}
              type="line"
              loading={loading.messages}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AnalyticsChart
              title="Message Role Distribution"
              data={messageRoleChartData}
              type="pie"
              loading={loading.messages}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <StatusChangesSection loading={loading.statusChanges} />

        <Divider sx={{ my: 4 }} />

        <Box sx={{ position: 'relative' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Feedback Analytics
          </Typography>

          {/* Blurred content */}
          <Box sx={{ filter: 'blur(5px)', pointerEvents: 'none' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Positive Feedback"
                  value={0}
                  icon={<ThumbUpIcon />}
                  loading={false}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Negative Feedback"
                  value={0}
                  icon={<ThumbDownIcon />}
                  loading={false}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title="Total Feedback"
                  value={0}
                  icon={<ThumbUpIcon />}
                  loading={false}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 150, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{ height: '100%', width: '100%' }}></Box>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Coming Soon overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: 1,
              backdropFilter: 'blur(1px)',
              zIndex: 10,
            }}
          >
            <Chip
              icon={<UpdateIcon />}
              label="Coming Soon"
              color="primary"
              sx={{
                fontSize: '1rem',
                py: 2.5,
                px: 2,
                mb: 2,
                '& .MuiChip-label': { px: 1 },
                '& .MuiChip-icon': { fontSize: '1.5rem' }
              }}
            />
            <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: '80%' }}>
              Feedback analytics features are under development and will be available in a future update.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default AnalyticsDashboard; 